(ns app.front.view.media-attachment.list
  (:require [app.domain.attachment.model :as attachment-model]
            [app.domain.group.config.model :as config-model]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.file.upload :as upload]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.section :as section]
            [app.front.util :as futil]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; Util

(defn content-type-description
  "Returns the description for the content-type defined in the dictionary"
  [{:file/keys [content-type] :as file} tr]
  (cond (attachment-model/image? file) (tr [:content-type-description/image])
        (= content-type "application/pdf") (tr [:content-type-description/pdf])
        :else (tr [:content-type-description/document])))

(defn iso-date [date]
  (.format date-util/calendar-date-format date))

(defn set-drag-styles
  "Styling for the container during the drag over"
  [*drag-flag on?]
  (let [id           "media-wrapper"
        e            (.getElementById js/document id)
        curr-classes (-> e (.getAttribute "class") (str/split " "))]
    (if on?
      (do
        (swap! *drag-flag inc)
        (.setAttribute e "class" (str/join " " (distinct (conj curr-classes "media-attachment-drop-area")))))
      (when (zero? (swap! *drag-flag dec))
        (.setAttribute e "class" (str/join " " (remove #(= % "media-attachment-drop-area") curr-classes)))))))

(defn select-date
  "Selects the correct date from the timeline based on the scroll event"
  [scroll-from-navigation? e]
  (let [position           (-> e .-target .-scrollLeft)
        date-wrappers      (.getElementsByClassName js/document "media-wrapper-item")
        first-date-visible (some->> date-wrappers
                                    (filter #(< position (+ (.-offsetLeft %) (.-offsetWidth %))))
                                    (sort-by #(.-offsetLeft %))
                                    first
                                    .-id)]
    ;; don't reselect the date when navigated from the date selection
    (if scroll-from-navigation?
      (>evt [::unset-scroll-from-navigation])
      (>evt [::set-selected-date first-date-visible]))))

;; Effects

(rf/reg-fx ::scroll-to-element-by-id
  (fn [id]
    (r/after-render
      (fn []
        (p/if-all-let [media-wrapper (.getElementById js/document "media-wrapper")
                       anchor        (.getElementById js/document id)]
          (do (.scroll media-wrapper (clj->js {:left (.-offsetLeft anchor)}))
              nil))))))

(rf/reg-fx ::scroll-to-the-end
  (fn []
    (r/after-render
      (fn []
        (when-let [media-wrapper (.getElementById js/document "media-wrapper")]
          (.scroll media-wrapper (clj->js {:left (.-scrollWidth media-wrapper)}))
          nil)))))

;; Events

(rf/reg-event-db ::set-file-to-delete
  (fn [db [_ file]]
    (assoc db ::file-to-delete file)))

(rf/reg-event-db ::saving-new-attachment
  (fn [db _]
    (assoc db ::saving-new-attachment? true)))

(rf/reg-event-db ::set-selected-attachment
  (fn [db [_ attachment-id]]
    (assoc db ::selected-attachment attachment-id)))

(rf/reg-event-db ::unset-selected-attachment
  (fn [db _]
    (dissoc db ::selected-attachment)))

(rf/reg-event-db ::set-selected-date
  (fn [db [_ date]]
    (assoc db ::selected-date date)))

(rf/reg-event-fx ::scroll-to-selected-date
  (fn [{:keys [db]} [_ date]]
    {:fx [[::scroll-to-element-by-id date]]
     :db (-> db
             (assoc ::selected-date date)
             (assoc ::scroll-from-navigation? true))}))

(rf/reg-event-db ::unset-scroll-from-navigation
  (fn [db _]
    (dissoc db ::scroll-from-navigation?)))

(rf/reg-event-fx ::media-attachment-save-success
  (fn [{:keys [db]} [_ file-id status resp]]
    (let [merged-data (assoc resp file-id {:recently-added [:assoc true]})]
      {:fx [[:dispatch [::entities/apply-changes status merged-data]]
            [::scroll-to-the-end]]
       :db (-> db
               (assoc ::selected-date (iso-date (date-util/now)))
               (dissoc ::saving-new-attachment?))})))

(rf/reg-event-fx ::media-attachment-uploaded
  (fn [_ [_ process-target status response]]
    (let [file-id (ffirst response)]
      {:fx [[:dispatch [::entities/merge-entity-db status response]]
            [:dispatch [::process/signal
                        process-target
                        [:process.signal/add-media-attachment {:file-id file-id}]
                        {:on-done [::media-attachment-save-success file-id]
                         :notify  {:success :media-attachment/upload-success}}]]]})))

(rf/reg-event-fx ::set-media-attachment-description
  (fn [_ [_ file-id value]]
    {:comm/command {:command    :file/edit-workspace-file-description
                    :method     :post
                    :data       {:file-id          file-id
                                 :file/description value}
                    :on-success [::entities/apply-changes]
                    :notify     {:success :media-attachment/update-data-success}}}))

(rf/reg-event-fx ::delete-success
  (fn [_ [_ status response]]
    {:fx [[:dispatch [::entities/apply-changes status response]]
          [:dispatch [::unset-selected-attachment]]]}))

(rf/reg-event-fx ::delete-attachment
  (fn [_ [_ file]]
    {:fx           [[:dispatch [::unset-selected-attachment]]]
     :comm/command {:command    :file/delete-workspace-file
                    :method     :post
                    :data       {:file-id (:db/id file)}
                    :on-success [::delete-success]
                    :notify     {:success :media-attachment/delete-success}}}))

(rf/reg-event-fx ::unlink-photo
  (fn [_ [_ file target]]
    {:fx           [[:dispatch [::unset-selected-attachment]]]
     :comm/command {:command    :file/unlink-complaint-photo
                    :method     :post
                    :data       {:file-id (:db/id file)
                                 :target  target}
                    :on-success [::delete-success]
                    :notify     {:success :media-attachment/delete-success}}}))

;; Subscriptions


(rf/reg-sub ::media-and-inspection-attachments
  :<- [::entities/context]
  (fn [ctx [_ phase-id inspection-ids]]
    (entities/find-entities ctx (filter #(and (->> % :entity/parent (contains? (conj (set inspection-ids) phase-id)))
                                              (->> % :file/category (contains? #{:file.category/inspection-attachment
                                                                                 :file.category/media-attachment})))))))

(def permit-types-with-media-attachments #{:permit-type/CE})

(rf/reg-sub ::media-attachments-enabled-for-permit-type?
  (fn [[_ phase-id]]
    (rf/subscribe [::entities/entity phase-id]))
  (fn [phase]
    (->> phase
         :entity/parent
         :workspace/permit-type
         (contains? permit-types-with-media-attachments))))

(rf/reg-sub ::show-media-attachments-section?
  (fn [[_ phase-id inspection-ids]]
    [(rf/subscribe [::media-attachments-enabled-for-permit-type? phase-id])
     (rf/subscribe [::media-and-inspection-attachments phase-id inspection-ids])
     (rf/subscribe [::process/signal-permitted? phase-id [:process.signal/add-media-attachment]])])
  (fn [[enabled? media-attachments add-permitted?] _]
    (and enabled?
         (or (seq media-attachments) add-permitted?))))

(rf/reg-sub ::file-to-delete :-> ::file-to-delete)
(rf/reg-sub ::selected-attachment :-> ::selected-attachment)
(rf/reg-sub ::saving-new-attachment? :-> ::saving-new-attachment?)
(rf/reg-sub ::selected-date :-> ::selected-date)
(rf/reg-sub ::scroll-from-navigation? :-> ::scroll-from-navigation?)

(rf/reg-sub ::complaint-category-name
  (fn [[_ file-id]]
    [(rf/subscribe [::entities/entity file-id])
     (rf/subscribe [:language])])
  (fn [[file lang] _]
    (-> file
        :entity/parent
        :complaint/category-group-config
        (config-model/localized-name lang))))

;; Views


(defn upload-area [on-attach content]
  [upload/upload-area
   "media-attachment-upload"
   {:attach-file-fn     on-attach
    :additional-help    :inspection/upload-help
    :hide-styles        true
    :additional-content content
    :accept-types       attachment-model/allowed-media-types}])

(defn- add-media-attachment [target file-id filename content-type md5]
  (>evt [::saving-new-attachment])
  (comm/send-command-execution
    {:command    :file/add-workspace-file
     :data       (merge {:workspace-id      (-> target :entity/parent :workspace/id)
                         :file-id           file-id
                         :file/filename     filename
                         :file/content-type content-type
                         :file/category     :file.category/media-attachment
                         :file/md5          md5
                         :jpeg+thumbnail?   true}
                        (when (= content-type "application/pdf")
                          {:convert-to-pdfa? true
                           :async-pdfa?      true}))
     :on-success [::media-attachment-uploaded target]}))

(defn field-block [key value]
  [:div
   [:label.bx--label key]
   [:div.mb-4 value]])

(defn info-dialog [{:keys [tr permitted?]}]
  (let [file-id                                        (<sub [::selected-attachment])
        {:file/keys   [filename description]
         :entity/keys [creator created]      :as file} (<sub [::entities/entity file-id])
        cancel-fn                                      #(>evt [::unset-selected-attachment])
        open?                                          (boolean file-id)
        complaint-category-name                        (<sub [::complaint-category-name file-id])
        default-description                            (or description complaint-category-name)]
    [ui-modal/composed-input-modal
     {:open       open?
      :on-close   cancel-fn
      :class-name "full-width-modal"}
     (cc/modal-header {:close-modal cancel-fn
                       :title       (tr [:media-attachment/dialog-title])})
     (when open?
       (cc/modal-body
         {:class-name "overflow-visible"}
         [field-block (tr [:file/filename]) filename]
         [field-block (tr [:file/filetype]) (content-type-description file tr)]
         [field-block (tr [:file/uploaded]) (str ((<sub [::date-util/date-fn]) created) " "
                                                 (user-model/full-name creator))]
         [field-block (tr [:description]) (if permitted?
                                            [ui/text-area
                                             {:label-text    ""
                                              :hide-label    true
                                              :default-value default-description
                                              :light         false
                                              :on-change     #(rf/dispatch-sync [:core.front.event/input-change
                                                                                 [::set-media-attachment-description
                                                                                  file-id
                                                                                  (futil/tval %)]])}]
                                            description)]))
     (cc/modal-footer
       {}
       (when permitted?
         (cc/button {:kind     :secondary
                     :icon     i/trash
                     :on-click #(>evt [::set-file-to-delete file])}
                    (tr [:media-attachment/delete])))
       (cc/button {:kind     :primary
                   :on-click cancel-fn}
                  (tr [:close])))]))

(defn attachment-item [{:keys [tr]} {:file/keys [description category conversion-pending?] :as file}]
  (let [image?                  (attachment-model/image? file)
        file-id                 (:db/id file)
        complaint-photo?        (= category :file.category/complaint-photo)
        complaint-photo-date    ((<sub [::date-util/date-fn]) (:entity/created file))
        complaint-category-name (<sub [::complaint-category-name file-id])
        open-info               (fn [e]
                                  (.preventDefault e)
                                  (.stopPropagation e)
                                  (>evt [::set-selected-attachment file-id]))]
    [:div.rounded-t.media-item
     [:div.flex.flex-column.relative.w-full
      (if conversion-pending?
        [:div.w-full.h-48.bg-ui-light-gray.flex.flex-col.items-center.justify-center.gap-4
         (cc/loading-spinner {:with-overlay false})
         [:div.text-xs.font-bold (tr [:file/generating-thumbnail])]]
        [:a.w-full
         {:target "_blank"
          :href   (file-download/download-href :file/download-workspace-file
                                               {"file-id" file-id})}
         (if image?
           [:img.w-full.h-48.object-cover.block
            {:alt (tr [:media-attachment/open-file])
             :src (file-download/download-href :file/download-workspace-file
                                               {"file-id" file-id
                                                "preview" true})}]
           (cc/icon {:icon        i/file
                     :description (tr [:media-attachment/open-file])
                     :class-name  "w-full h-48 object-cover p-12"}))])
      [:a.absolute.bg-blue-diagonal-left.left-0.w-16.h-16.pl-2.pt-2
       {:href (file-download/download-href :file/download-workspace-file
                                           {"file-id"  file-id
                                            "download" true})}
       (cc/icon {:icon        i/download
                 :description (tr [:inspection/download-file])
                 :class-name  "cursor-pointer"
                 :height      "14"
                 :width       "14"})]
      [:span.absolute.bg-blue-diagonal.w-14.h-16.right-0.w-16.text-right.pr-2.pt-2
       {:on-click open-info}
       (cc/icon {:icon        i/circle-info
                 :description "Information"
                 :class-name  "cursor-pointer"
                 :height      "16"
                 :width       "16"})]]
     [:div.relative.w-full.p-3.bg-ui-night-sky-100.text-white.h-14
      [:div.font-light.mb-1 (if complaint-photo?
                              (tr [:file-type-description/complaint-photo])
                              (content-type-description file tr))]
      [:div.truncate.h-10
       (or description
           (if complaint-photo?
             (str complaint-category-name ", " complaint-photo-date)
             [:a.underline.text-white.font-light.italic.cursor-pointer.text-xs
              {:on-click open-info}
              (tr [:media-attachment/add-description])]))]
      (when (:recently-added file)
        [:span.absolute.leading-10.right-0.-bottom-4.w-10
         (cc/icon {:icon        i/check
                   :description (tr [:media-attachment/successfully-added])
                   :class-name  "added-file-mark"})])]]))

(defn section-content [{:keys [tr format-date] :as props} {:keys [dates+files dates]}]
  (r/with-let [;; this helps to keep in track when the on-drag-leave event is fired by the parent container
               *drag-flag (r/atom 0)]
    (let [selected-date     (or (<sub [::selected-date]) (first dates))
          selected-date-idx (p/find-index dates selected-date)
          show-in-mobile?   #(or (= selected-date-idx %)
                                 (= (- selected-date-idx 1) %)
                                 (= (+ selected-date-idx 1) %))
          on-drop           (partial set-drag-styles *drag-flag false)
          display-date      #(format-date (date-util/parse-local-date %))]
      [:div.relative
       {:on-drag-enter (partial set-drag-styles *drag-flag true)
        :on-drag-leave on-drop
        :on-drop       on-drop}
       (if (seq dates)
         [:<>
          [:div.flex.flex-row.flex-wrap.bg-gray-100.rounded-md.mt-4
           (futil/for-frag-indexed [[idx date-item] dates]
             [:div.date-item.flex-auto.text-center.cursor-pointer.leading-8.rounded-md.font-bold
              {:class    [(when (= selected-date date-item) "bg-ui-blue-20")
                          (when (show-in-mobile? idx) "mobile-date-item")]
               :on-click (fn [_]
                           (>evt [::scroll-to-selected-date date-item]))}
              (display-date date-item)])]
          [:div.media-wrapper
           {:id        "media-wrapper"
            :on-scroll (partial select-date (<sub [::scroll-from-navigation?]))}
           (futil/for-frag [date-row dates]
             (let [attachment-items (get dates+files date-row)
                   items-count      (count attachment-items)]
               [:div.media-wrapper-item
                {:id date-row}
                [:h5.mb-2 (str (display-date date-row) " (" items-count ")")]
                [:div.media-items
                 {:class (cond (< items-count 3) "cols-1"
                               (< items-count 5) "cols-2"
                               (< items-count 7) "cols-3")}
                 (futil/for-frag [attachment (sort-by :entity/created attachment-items)]
                   [attachment-item props attachment])]]))]]
         [:div.media-wrapper.media-attachment-drop-area.h-20.text-center.pt-6
          (tr [:media-attachment/drop-area-info])])
       (when (<sub [::saving-new-attachment?])
         [:div.save-overlay.rounded-md.text-center
          (cc/loading-spinner {:class-name   "absolute left-1/2 top-1/2"
                               :small        true
                               :with-overlay false})])])))

(defn list-view [{:keys [tr target inspection-ids] :as props}]
  (let [format-date      (<sub [::date-util/date-fn])
        now              (date-util/now)
        complaint-photos (:execution/selected-complaint-photos target)
        attachments      (<sub [::media-and-inspection-attachments (:db/id target) inspection-ids])
        dates+files      (->> (concat complaint-photos attachments)
                              (group-by #(iso-date (or (:entity/created %) now))))
        dates            (sort (keys dates+files))
        props+           (assoc props :format-date format-date)
        permitted?       (<sub [::process/signal-permitted?
                                target
                                [:process.signal/add-media-attachment]])
        file-to-delete   (<sub [::file-to-delete])
        complaint-photo? (= (:file/category file-to-delete) :file.category/complaint-photo)]
    [:div
     {:id "media-attachments-container"}
     (if permitted?
       [upload-area (partial add-media-attachment target)
        [section-content props+ (util/mmap dates+files dates)]]
       [section-content props+ (util/mmap dates+files dates)])
     (if file-to-delete
       [ui/confirm-modal {:tr           tr
                          :id           "delete-media-attachment"
                          :modal-title  (tr [:remove])
                          :open?        (boolean file-to-delete)
                          :on-close     #(>evt [::set-file-to-delete nil])
                          :on-yes-click #(>evt (if complaint-photo?
                                                 [::unlink-photo file-to-delete (:db/id target)]
                                                 [::delete-attachment file-to-delete]))}
        (tr [:media-attachment/delete-confirm])]
       [info-dialog (assoc props :permitted? permitted?)])]))

(defn media-attachment-section [{:keys [target title] :as props}]
  [section/section
   "media-attachments"
   (assoc props
          :heading-button [upload/upload-button "media-attachment"
                           {:disabled?       (not (<sub [::process/signal-permitted?
                                                         target
                                                         [:process.signal/add-media-attachment]]))
                            :attach-file-fn  (partial add-media-attachment target)
                            :button-i18n-key :attachments/add-new
                            :multiple        true}])
   title
   [list-view props]])
